import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Pencil, Send, MessageCircleHeart, Lock, Shirt } from "lucide-react";
import { COSTUMES, costumeUnlocked, NAME_REPLIES, CHAT_REPLIES } from "../../data/mochi";

const pickOne = (a) => a[Math.floor(Math.random() * a.length)];

// Kolom obrolan: pasangan mengetik; jika nama Mochi disebut, Mochi merespons (onCall) — selain itu balasan ringan.
export function NameChat({ name, onRename, onCall, onChat }) {
  const [msgs, setMsgs] = useState([{ from: "mochi", text: `Halo! Panggil aku "${name}" ya~` }]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const listRef = useRef();

  useEffect(() => { listRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }); }, [msgs]);
  useEffect(() => { setDraft(name); }, [name]);

  const send = () => {
    const t = text.trim();
    if (!t) return;
    const called = t.toLowerCase().includes(name.toLowerCase());
    const reply = called ? pickOne(NAME_REPLIES) : pickOne(CHAT_REPLIES);
    setMsgs((m) => [...m.slice(-11), { from: "you", text: t }, { from: "mochi", text: reply }]);
    setText("");
    if (called) onCall(reply); else onChat(reply);
  };
  const saveName = () => {
    const n = draft.trim();
    if (n.length < 2) return toast.error("Nama minimal 2 huruf");
    onRename(n); setEditing(false);
    setMsgs((m) => [...m, { from: "mochi", text: `Yeay, sekarang aku dipanggil ${n}! 🐾` }]);
    toast.success(`Nama peliharaan diganti jadi ${n}`);
  };

  return (
    <div className="rounded-3xl border border-[#efeae4] bg-white p-5" data-testid="name-chat-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[16px] font-bold"><MessageCircleHeart className="h-4 w-4 text-[#ff5a5f]" /> Ngobrol sama {name}</h2>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={16} data-testid="pet-name-input"
              onKeyDown={(e) => e.key === "Enter" && saveName()}
              className="w-28 rounded-full border border-[#eee5da] bg-[#fcfaf7] px-3 py-1.5 text-[12px] font-semibold outline-none focus:border-[#ff5a5f]" />
            <button onClick={saveName} data-testid="pet-name-save" className="rounded-full kk-coral-gradient px-3 py-1.5 text-[12px] font-bold text-white">Simpan</button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} data-testid="pet-name-edit"
            className="flex items-center gap-1 rounded-full bg-[#ffebeb] px-3 py-1.5 text-[12px] font-bold text-[#ff5a5f] hover:bg-[#ffd9d9]">
            <Pencil className="h-3 w-3" /> Ganti nama
          </button>
        )}
      </div>
      <div ref={listRef} className="mb-3 max-h-44 space-y-1.5 overflow-y-auto rounded-2xl bg-[#faf6f1] p-3" data-testid="chat-list">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.from === "you" ? "justify-end" : "justify-start"}`}>
            <span data-testid={`chat-msg-${m.from}`}
              className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[13px] ${m.from === "you" ? "kk-coral-gradient text-white" : "bg-white text-[#4b3f38] shadow-sm"}`}>
              {m.text}
            </span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={`Coba ketik "${name}, sini dong!"`} data-testid="chat-input"
          className="flex-1 rounded-full border border-[#eee5da] bg-[#fcfaf7] px-4 py-2.5 text-[13px] outline-none focus:border-[#ff5a5f]" />
        <button onClick={send} data-testid="chat-send" className="grid h-10 w-10 place-items-center rounded-full kk-coral-gradient text-white active:scale-95">
          <Send className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-2 text-[11px] text-[#a69995]">Sebut namanya di pesan dan {name} akan menoleh, mengeong, dan menjawab.</p>
    </div>
  );
}

export function CostumeCloset({ level, outfit, onEquip, cats = [] }) {
  return (
    <div className="rounded-3xl border border-[#efeae4] bg-white p-5" data-testid="costume-closet">
      <h2 className="mb-3 flex items-center gap-2 text-[16px] font-bold"><Shirt className="h-4 w-4 text-[#ff5a5f]" /> Lemari kostum musiman</h2>
      <div className="grid grid-cols-4 gap-2">
        <button onClick={() => onEquip(null)} data-testid="costume-none"
          className={`rounded-2xl border p-2 text-center transition-colors ${!outfit ? "border-[#ff5a5f] bg-[#ffebeb]" : "border-[#efeae4] bg-[#fcfaf7]"}`}>
          <div className="mx-auto mb-1 grid h-8 place-items-center text-[20px]">🐱</div>
          <div className="text-[10px] font-semibold leading-tight text-[#6e594c]">Polos</div>
        </button>
        {COSTUMES.map((c) => {
          const ok = costumeUnlocked(c, level, cats);
          const on = outfit === c.id;
          return (
            <button key={c.id} data-testid={`costume-${c.id}`}
              onClick={() => ok ? onEquip(c.id) : toast(c.dateCategory ? `Selesaikan kencan bertema ${c.dateCategory} untuk membuka ${c.label}` : `Buka di level ${c.reqLevel} atau saat ${c.note.toLowerCase()}`)}
              className={`relative rounded-2xl border p-2 text-center transition-colors ${on ? "border-[#ff5a5f] bg-[#ffebeb]" : "border-[#efeae4] bg-[#fcfaf7]"} ${!ok ? "opacity-55" : ""}`}>
              <div className="mx-auto mb-1 grid h-8 place-items-center text-[20px]">{c.emoji}</div>
              <div className="text-[10px] font-semibold leading-tight text-[#6e594c]">{c.label}</div>
              {!ok && <Lock className="absolute right-1.5 top-1.5 h-3 w-3 text-[#a99]" />}
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-[#a69995]">Kostum musiman terbuka saat musimnya tiba / level tercapai. <b>Kupon kencan</b> (🧭🍳🎩) terbuka setelah menyelesaikan kencan Wisata Alam, Restaurant, atau Museum.</p>
    </div>
  );
}
